/* eslint-disable max-lines */
import Animal from '../constants/animal'
import type { DosageType } from '../constants/dosageType'
import type { DrugDeliveryAction } from '../constants/drugDeliveryMethod'
import ResponsibilityName from '../constants/responsibilityNames'
import RoleName from '../constants/roleNames'
import {
  CustomEmailValidationType,
  EmailValidationType,
} from '../constants/users'
import { MembershipStatus } from '../constants/wellnessPlans'
import { BusinessRole, Responsibility, Role } from './roles'
import { CountryCode, IdObject, Language, Nil } from './types'

export type BlobWithName = Blob & {
  name: string
}

export type FileTemplate = {
  body?: string
  data?: string
  description?: string
  extension?: string | Nil
  fileName?: string | Nil
  fileUrl?: string | Nil
  id?: string | Nil
  name?: string | Nil
  raw?: BlobWithName
  required?: boolean
  typeId?: string
}

export type DocumentFile = {
  file: FileTemplate
  sign: boolean
  signer: string
}

export type AddressParams = {
  address?: string
  city?: string
  country?: CountryCode
  state?: string
  suite?: string
  zip?: string
  zipcode?: string
}

export enum UserInvitationStatus {
  ACCEPTED = 'ACCEPTED',
  PENDING = 'PENDING',
  EXPIRED = 'EXPIRED',
  IS_NOT_SENT = 'IS_NOT_SENT',
  NONE = 'NONE',
}

export type BaseUser = {
  firstName?: string
  lastName?: string
}

export type UserRole = {
  person: User
  role: Role
}

export type EmergencyContact = BaseUser & {
  homePhone?: string
  mobilePhone?: string
  otherPhone?: string
  relationship?: string | Nil
  workPhone?: string
}

export type EmployeeIdInfo = {
  duplicate: boolean
  employeeIdNumber: number
  employeeIdType: string | Nil
  generatedEmployeeIdNumber: boolean
  id: string
  issueDate: string
  issuingAuthority: string | Nil
  notes: string | Nil
}

export type PersonBusinessAlert = {
  alertColorId: string
  alertText: string
  businessId: string
  personId: string
}

type UserScope = {
  currentBusinessId?: string
  currentBusinessRootId?: string
  currentGroupBusinessIds?: string[]
  currentHierarchyBusinessIds?: string[]
}

export type UserChewy = {
  hasKyriosId?: boolean
}

export type GlobalClientPreferences = {
  communicationSmsOptIn: boolean | null
}

export type User = BaseUser &
UserScope &
AddressParams &
UserChewy & {
  active?: boolean
  additionalEmail?: string
  alertColorId?: string | Nil
  alertText?: string
  avatar?: string
  avatarThumbnail?: string
  balance?: number
  businessId?: string | Nil
  businessToRoleList?: BusinessRole[]
  clientInContextBusinesses?: string[] | Nil
  coparents?: string[] | Nil
  county?: string
  creationDate?: string
  dateOfBirth?: string
  dea?: string
  deleted?: boolean
  departmentId?: string
  discountGroup?: string
  email?: string
  emailValidationResultType?:
  | EmailValidationType
  | CustomEmailValidationType
  | Nil
  emailValidationSuggestion?: string | Nil
  emailVerificationDate?: string
  emergencyContact?: EmergencyContact | Nil
  employeeIdInfos?: EmployeeIdInfo[]
  eulaAcceptanceDate?: string
  externalPersonId?: string
  externalSource?: string
  favouriteBusinessIds?: string[]
  genderId?: string
  globalClientPreferences?: GlobalClientPreferences
  hasBalanceInOtherGroupBusinesses?: boolean
  homePhone?: string
  id: string
  intercomHash?: string
  invitationStatus?: UserInvitationStatus | Nil
  isBoopUser?: boolean
  isShared?: boolean
  licenses?: BusinessLicense[]
  locationPickerPinned?: boolean
  lockWorkingHoursSettings?: {
    businessId: string
    lockOutsideWorkingHours: boolean
    locked: boolean
    timeToLock: number
  }[]
  mobilePhone?: string | Nil
  notes?: string
  otherPhone?: string
  patients?: string[] | Nil
  personBusinessAlert?: PersonBusinessAlert | Nil
  photo?: string
  photoThumbnail?: string
  preferredContactMethod?: string
  preferredContactMethodId?: string
  promoteWellnessPlans?: boolean
  referralSourceId?: string
  referralSourceTypeId?: string
  sendReminders?: boolean
  signatureUrl?: string | Nil
  ssoUser?: boolean
  tag?: string
  taxIdentificationNumber?: string
  team?: boolean
  timeTrackingSettings?: {
    business: string
    trackingEnabled: boolean
  }[]
  workPhone?: string
  znlabsProviderIdentifier?: string
}

export type UserWithFullPatients = Omit<User, 'patients'> & {
  patients?: Patient[]
}

export type Coparent = BaseUser &
AddressParams & {
  email: string
  homePhone?: string
  id: string
  mobilePhone?: string
  notes?: string
  otherPhone?: string
  preferredContactMethod?: string
  preferredContactMethodId?: string
  tag?: string
  workPhone?: string
}

export type PatientMembership = {
  wplanBusinessId: string
  wplanCancellationDate: string | null
  wplanClientId: string
  wplanId: string
  wplanLevel: number
  wplanLogId: string
  wplanName: string
  wplanSinceDate: string
  wplanStatus: MembershipStatus
  wplanTypeId: string
  wplanVersion: number
  wplanVersionId: string
}

export type PatientShortTimetableEvent = {
  appointmentId: string
  startDate: string
  typeName: string
}

export type PatientChewy = {
  hasKyriosId?: boolean
}

export type Patient = PatientChewy & {
  acquiredDate?: string
  acquiredType?: string
  active?: boolean
  activityLevel?: string
  alertColorId?: string | Nil
  alertTypeIds?: string[]
  alertTypes?: string[]
  allergies?: string
  approximateAge?: boolean
  approximateDateOfBirth?: boolean
  breeds?: string[]
  color?: string
  currentWeight?: {
    date: string
    value: number
  }
  customAlertType?: string
  dateOfBirth?: string
  deceased?: boolean
  deceasedDate?: string
  diet?: string
  environment?: string
  externalPatientId?: string
  externalSource?: string
  gender?: string
  goodToKnow?: string
  id: string
  insurancePolicyNumber?: string
  insuranceProvider?: string
  isBoop?: boolean
  lastAppointment?: PatientShortTimetableEvent
  license?: string
  markingsTattoo?: string
  microchipNumber?: string
  modificationDate?: string
  name: string
  nextAppointment?: PatientShortTimetableEvent
  notes?: string
  patientScopeGoodToKnows?: { businessId: string; goodToKnow: string }[]
  photo?: string | Nil
  photoThumbnail?: string
  plans?: PatientMembership[]
  rabiesTag?: string
  rabiesTagExpiration?: string
  referralSourceId?: string
  referralSourceTypeId?: string
  schedule?: string
  sendReminders?: boolean
  serviceAnimalType?: string
  size?: string
  spayedNeutered?: boolean
  species?: string
  speciesId?: string
  weight?: {
    date: string
    value: number
  }
  wplanCancellationDate?: string | Nil
}

export type Contact = {
  email: string
  id: string
  name: string
  phone: string
}

export type AppointmentCommunicationTemplate = {
  active: boolean
  appointmentTypeIds?: string[]
  communicationMethodId: string
  communicationMethodPrimaryBackupId: string | Nil
  communicationMethodSecondaryBackupId: string | Nil
  communicationMethodTertiaryBackupId: string | Nil
  id: string
  initializationByStatus: { triggeringStatusId: string } | Nil
  initializationByTime: {
    offsetPointId?: string
    timeOffset: {
      amount: 1
      unit?: string
    }
  } | null
  initializationTypeId: string
  name: string
  subject: string
  template: string | null
  textTemplate: string | null
}

export type AppointmentCommunicationsConfiguration = {
  appointmentInfoMessage?: string
  newAppointmentAutoEmailEnabled?: boolean
  newAppointmentMessage?: string
  templates?: AppointmentCommunicationTemplate[]
}

export type AppointmentConfiguration = {
  appointmentTypeId: string
  color: string | Nil
  defaultDuration?: string
  enabled: boolean
  onlineSchedulingAllowed: boolean
}

export type CommunicationSettings = {
  afterHoursMessage: {
    enabled: boolean
    message: string
  }
  openHoursMessage: {
    enabled: boolean
    message: string
  }
}

export type DefaultReminderExpirationOffset = {
  amount: number
  unit: string
}

export type LabelTemplates = {
  cageCard: string | Nil
  cageCardAlert: string | Nil
  folder: string | Nil
  labOrder: string | Nil
  practiceAddress: string | Nil
  prescription: string | Nil
}

export type TelehealthSettings = {
  linkMessage?: string
}

export type SocialMedia = {
  facebook?: string
  instagram?: string
  twitter?: string
}

export type BusinessAutoReplySettings = {
  boopAutoReplySettings?: CommunicationSettings
  emailAutoReplySettings?: CommunicationSettings
  smsAutoReplySettings?: CommunicationSettings
}

export type BusinessSsoIdpSettings = {
  id: string
  ssoDefaultRoleId: string
  ssoIdpAppId: string
  ssoIdpDomain: string
}

export type BusinessBrandingConfiguration = {
  backgroundColor?: string
  buttonsColor?: string
  customColorsEnabled?: boolean
}

export type BusinessInternalChatOnboard = {
  chatIntegrationCompleted?: boolean
  chatUiEnabled?: boolean
}

export type BusinessChewy = {
  hasKyriosId?: boolean
  kyriosId?: string
}

export type BusinessTax = {
  id: string
  name: string
  rateValue: number
}

export type BusinessLicense = {
  additionalInfo?: string
  expirationDate?: string
  id?: string
  issueDate?: string
  issuingAuthority?: string
  number?: string
  rank?: string
  type?: string
  typeId?: string
}

export type BusinessDiscountConfigurationItem = {
  active: boolean
  id?: string
  inventoryDiscount?: number
  labsDiscount?: number
  name: string
  proceduresDiscount?: number
}

export type BusinessDiscountConfiguration = {
  items?: BusinessDiscountConfigurationItem[]
  restrictApplication: boolean | Nil
}

export type Business = AddressParams &
BusinessAutoReplySettings &
BusinessBrandingConfiguration &
BusinessChewy &
BusinessInternalChatOnboard & {
  appointmentCancellationReasonEnabled?: boolean
  appointmentCommunicationsConfiguration?: AppointmentCommunicationsConfiguration
  appointmentConfiguration?: AppointmentConfiguration[]
  appointmentConfigurationByTypeId?: Record<string, AppointmentConfiguration>
  autoAssignEvent?: boolean
  autoGenerateVaccineCertificates?: boolean
  boopCommunicationSettings?: CommunicationSettings
  boopVariationId?: string
  children?: any[]
  clientAlertColorId?: string
  clientsCommunicationEnabled?: boolean
  communicationsPhone?: string
  counties?: string[]
  countryCatalogCode?: CountryCode
  currencyId?: string
  customColorsEnabled?: boolean
  defaultReminderExpirationOffset?: DefaultReminderExpirationOffset
  devExtensionsEnabled?: boolean
  discountConfiguration?: BusinessDiscountConfiguration
  discountReasonMandatory?: boolean
  dymoConnectEnabled?: boolean
  email: string
  estimateExpiration?: string
  estimateExpirationOffset?: number
  estimateExpirationTimeUnitId?: string
  estimateNotes?: string
  estimateRequiredDepositPerc?: number
  estimateRequiresDeposit?: boolean
  estimateSignatureOnApproval?: boolean
  estimatedWaitTimeInKioskEnabled?: boolean
  googleReviewLink?: string
  groupSharingModeId?: string
  groupTags?: IdObject[]
  hasActiveWellnessPlans?: boolean
  hasWPackages?: boolean
  id: string
  idexxImagingEnabled?: boolean
  internalName?: string
  invoiceAutoPostEnabled?: boolean
  invoiceAutoPostOffset?: number
  invoiceAutoPostTimeUnitId?: string
  invoiceNotes?: string
  invoiceShowDeclinedItems?: boolean
  kioskEnabled?: boolean
  kioskLandingPageMessage?: string
  kioskLandingPageMessageEnabled?: boolean
  labelTemplates?: LabelTemplates
  licenses?: BusinessLicense[]
  logo?: string
  measurementTypeId?: string
  name: string
  npsEnabled?: boolean
  omniChannel?: boolean
  openBoop?: boolean
  parentBusinessId?: string
  patientAlertColorId?: string
  pdmpNpi?: string
  pdmpProviderId?: string
  phone?: string
  posPayEnabled?: boolean
  practiceTypeIds?: string[]
  prescriptionLabelDisclaimer?: string
  proofOfVaccinationFooter?: string
  rabiesCertificateFooter?: string
  region?: string
  sendReminders?: boolean
  soapAutoFinalizeEnabled?: boolean
  soapAutoFinalizeOffset?: number
  soapAutoFinalizeTimeUnitId?: string
  soapAutoTaskCreationEnabled?: boolean
  soapFinalizedAppointmentTypeIds?: string[]
  socialMedia?: SocialMedia
  ssoConfigured?: boolean
  ssoIdpSettings?: BusinessSsoIdpSettings
  statusId?: string
  supportedLanguageCodes?: string[]
  tags?: Required<IdObject>[]
  taxIdentificationNumber?: string
  taxRateLocal?: number
  taxRateOther1?: number
  taxRateOther2?: number
  taxRateState?: number
  taxRateTotal?: number
  taxes?: BusinessTax[]
  telehealthSettings?: TelehealthSettings
  telemedicineEnabled?: boolean
  telemedicineText?: string
  timeZone?: string
  useDefaultSignature?: {
    chewyPrescription: boolean
    otc: boolean
    proofOfVaccination: boolean
    rabiesVaccine: boolean
    soap: boolean
  }
  waitListEnabled?: boolean
  website?: string
  wellnessPlansEnabled?: boolean
  wellnessPlansInKioskEnabled?: boolean
  wellnessPlansInSchedulingEnabled?: boolean
  workingHours?: {
    from: string
    to: string
  }
  wplanSharingEnabled?: boolean
  /** @deprecated use v1/zoom/settings */
  zoomSettings?: ZoomSettings
}

export type ZoomSettings = {
  /** @deprecated */
  apiKey?: string
  /** @deprecated */
  apiSecret?: string
  businessId?: string
  includeLinkInKioskCheckin?: boolean
  integrationEnabled?: boolean
  zoomAccountId?: string
  zoomClientId?: string
  zoomClientSecret?: string
}

export type NamedEntity = {
  id: string
  name: string
  nameTranslation?: string
}

// Generic Constants
export type Constant = NamedEntity
export type ConstantWithColors = {
  color?: string
  colorVariant?: string
} & Constant

export type BaseSelectEntity = NamedEntity & {
  disabled?: boolean
  divider?: boolean
  pinColor?: string
  subheader?: boolean
}
export type NestedSelectEntity = BaseSelectEntity & { value: string | number }
export type SelectEntity = BaseSelectEntity & {
  subitems?: NestedSelectEntity[]
}
export type ConstantWithMeasurementsSystem = NamedEntity & {
  measurementSystemId: string
}

// Constants
export type BreedConstant = Record<string, NamedEntity[]>
export type ClientPatientSnapshotWidgetConstant = NamedEntity & {
  alias: string
}
export type CountryListConstant = {
  code: string
  code3: string
  creationDate?: string
  currencyId: string
  id: number
  languageId: string
  modificationDate?: string
  name: string
  numCode: number
}
export type DashboardLabTestStateConstant = ConstantWithColors & {
  labTestStateId: string
  orderStateId: string
}
export type DimensionsConstant = Record<
string,
Constant & { values: Constant[] }
>
export type DrugDeliveryMethodConstant = NamedEntity & {
  action: DrugDeliveryAction
}
export type DrugDosageTypeConstant = Omit<NamedEntity, 'name'> & {
  name: DosageType
}
export type EstimateStatesConstant = ConstantWithColors & {
  forbidSelect: boolean
}
export type FeatureTogglesConstant = {
  businessIds: string[]
  enabled: boolean
  name: string
}
export type ImagingModalitiesConstant = NamedEntity & {
  code: string
  fullName: string
}
export type InventorySubCategoryConstant = Record<string, Constant[]>
export type InsuranceProvidersPerCountryConstant = Partial<
Record<CountryCode, Constant[]>
>
export type LabelTemplatesHtmlConstant = Constant & {
  supportedDeviceTypes?: string[]
  supportedTypes?: string[]
}
export type LanguagesConstant = Language
export type LandingWidgetsConstant = NamedEntity & { alias: string }
export type LocationsConstant = Record<string, Constant>
export type MentationTypesConstant = Record<string, NamedEntity[]>
export type MeasurementTypesConstant = NamedEntity & {
  includedMeasurementSystemsIds: string[]
}
export type MucousMembraneColorsConstant = Record<string, NamedEntity[]>
export type MucousMembraneMoisturesConstant = Record<string, NamedEntity[]>
export type NotificationTypeConstant = NamedEntity & {
  notificationAreaId: string
}
export type OrderFiltersConstant = NamedEntity & {
  categoryId: string
  type?: string
}
export type PDMPReportVersionConstant = { name: string; version: string }
export type PlaceholderConstant = {
  group: string
  groupTranslation?: string
  items: Omit<NamedEntity, 'id'>[]
}
export type PracticeCloningItemsConstant = Constant & {
  dependencies: string[]
}
export type PulseCharacterTypesConstant = Record<string, NamedEntity[]>
export type CapillaryRefillTimeTypesConstant = Record<string, NamedEntity[]>
export type RabiesTagDefaultExpirationConstant = NamedEntity & {
  expiration: number
  expirationUnitId: string
}
export type ReminderProtocolTimeUnitsConstant = NamedEntity & { unit: string }
export type ReminderTemplateTimeUnitsConstant = NamedEntity & { unit: string }
export type SignatureOption = {
  id: string
  name: string
  signature: string
}
export type SkinTurgorsConstant = Record<string, NamedEntity[]>
export type SpeciesConstant = Omit<NamedEntity, 'name'> & {
  code: string
  humanReadable: string
  humanReadableTranslation: string
  name: Animal
}
export type TaskTemplateOptions =
  | 'dueDate'
  | 'dueTime'
  | 'endDate'
  | 'endTime'
  | 'teamMember'
  | 'team'
export type TaskTemplateOptionsConstant = Partial<
Record<TaskTemplateOptions, (NamedEntity & { enabled: boolean })[]>
>
export type TemperatureUnitsConstant = ConstantWithMeasurementsSystem
export type TimezoneConstant = NamedEntity & {
  abbreviation: string
  offset: string
  timezone: string
  timezoneTranslation: string
}
export type TimezoneGroupConstant = NamedEntity & {
  timezones: TimezoneConstant[]
}
export type VaccineDefaultDurationOfImmunityConstant = NamedEntity & {
  duration: number
  durationUnitId: string
}
export type WeightUnitsConstant = ConstantWithMeasurementsSystem
export type WorkFlowTypesConstant = NamedEntity & {
  shortName: string
  shortNameTranslation: string
}
export type WplanCoverAppointmentTypeConstant = NamedEntity & {
  appointmentTypes: string[]
}
export type WplanPriceTypeConstant = NamedEntity & {
  displayName: string
  displayNameTranslation: string
}

export type ProblemEnumValueConstant = NamedEntity & { order: number }
export type ProblemEnumConstant = NamedEntity & {
  values: ProblemEnumValueConstant[]
}

// EventType Constant
export enum EventTypeName {
  Appointment = 'Appointment',
  Busy = 'Busy',
  Reminder = 'Reminder',
  Task = 'Task',
  Marketing = 'Marketing',
  Lifecycle = 'Lifecycle',
}
export type BaseEventType<K extends EventTypeName, T = {}> = {
  id: string
  name: K
  states: NamedEntity[]
  subTypes: (NamedEntity & T)[]
}
export type AppointmentEventType = BaseEventType<
EventTypeName.Appointment,
{
  responsibilities: EventTypeAppointmentResponsibility[]
  roles: EventTypeAppointmentRole[]
}
>
export type BusyEventType = BaseEventType<EventTypeName.Busy>
export type ReminderEventType = BaseEventType<EventTypeName.Reminder>
export type TaskEventType = BaseEventType<EventTypeName.Task>
export type MarketingEventType = BaseEventType<EventTypeName.Marketing>
export type LifecycleEventType = BaseEventType<EventTypeName.Lifecycle>
export type EventTypeConstant =
  | [
    AppointmentEventType,
    BusyEventType,
    ReminderEventType,
    TaskEventType,
    MarketingEventType,
    LifecycleEventType,
  ]
  | []
export type EventTypeAppointmentResponsibility = Omit<NamedEntity, 'name'> & {
  name: ResponsibilityName
  roles: Role[]
}
export type EventTypeAppointmentRole = Omit<NamedEntity, 'name'> & {
  hidden: boolean
  name: RoleName
  permissions: object
  rhapsodyAnalytics: boolean
  type: string
}
export type WidgetAssignedModeConstant = {
  ALL?: string
  OWN?: string
  UNASSIGNED?: string
  UNASSIGNED_AND_OWN?: string
}

export type AppointmentCancellationReasonConstant = NamedEntity & {
  clientFacing?: boolean
}

export type ProblemState = NamedEntity & {
  diagnosisState: boolean
  findingState: boolean
  order: number
}

export type ConstantMap = {
  [Property in keyof Constants]: Constants[Property]
}

export type Constants = {
  AIAgentProcessEntityTypes: Constant[]
  AIAgentProcessStatuses: Constant[]
  AIAgentProcessTypes: Constant[]
  AcquiredType: string[]
  ActivityLevel: Constant[]
  AgeUnits: Constant[]
  AlertColor: Constant[]
  AlertType: string[]
  AnimalEnvironment: Constant[]
  AnswerInputTypes: Constant[]
  AntechLabLocations: Constant[]
  AppointmentCancellationReason: AppointmentCancellationReasonConstant[]
  AppointmentCommunicationInitializationType: Constant[]
  AppointmentCommunicationOffsetPoint: Constant[]
  AppointmentCommunicationTimeUnits: Constant[]
  AppointmentCommunicationTriggeringStatus: Constant[]
  AppointmentMissingPaymentMethodReasons: Constant[]
  AppointmentTypes: Constant[]
  AppointmentsWidgetAssignedMode: WidgetAssignedModeConstant
  AutoshipUnit: Constant[]
  AvailabilityRuleRecurrenceType: Constant[]
  AvailabilityRuleType: Constant[]
  BoopVariations: Constant[]
  Breed: BreedConstant
  BusinessRootId: string
  BusinessStatus: ConstantWithColors[]
  CapillaryRefillTime: CapillaryRefillTimeTypesConstant
  ClientPatientSnapshotWidgets: ClientPatientSnapshotWidgetConstant[]
  ClientRoleId: string
  ClockoutReason: Constant[]
  CommunicationTransportBackupOption: Constant[]
  CommunicationsWidgetAssignedMode: WidgetAssignedModeConstant
  ContactMethod: Constant[]
  ContactTypes: Constant[]
  ControlledSubstanceSchedules: Constant[]
  CountryList: CountryListConstant[]
  CreditAdjustmentReason: Constant[]
  CurrencyList: Constant[]
  CustomQuestionFrequencies: Constant[]
  DashboardLabTestStates: DashboardLabTestStateConstant[]
  DepartmentTypes: Constant[]
  DiagnosisProblemStates: ProblemState[]
  DiagnosisStatus: ConstantWithColors[]
  DictionaryScope: Constant[]
  Dimensions: DimensionsConstant
  DiscountSource: Constant[]
  DisplayLocations: Constant[]
  DocumentInputAction: Constant[]
  DocumentTypes: Constant[]
  DrugAdministrationFrequencies: Constant[]
  DrugDeliveryMethod: DrugDeliveryMethodConstant[]
  DrugDosageType: DrugDosageTypeConstant[]
  DrugStrengthColor: Constant[]
  DrugStrengthUnit: Constant[]
  EntityTypes: Constant[]
  EstimateStates: EstimateStatesConstant[]
  EventState: Constant[]
  EventType: EventTypeConstant
  EventTypeResponsibility: Responsibility[]
  ExpirationTimeUnits: Constant[]
  FearAnxietyScore: Constant[]
  FeatureToggles: FeatureTogglesConstant[]
  FinalizeTimeUnits: Constant[]
  FindingProblemStates: ProblemState[]
  FullAlertType: Constant[]
  Gender: Constant[]
  GroupSharingModes: Constant[]
  ImagingModalities: ImagingModalitiesConstant[]
  ImagingOrderStatuses: ConstantWithColors[]
  ImagingVendors: Constant[]
  InsuranceProvider: string[]
  InsuranceProvidersPerCountry: InsuranceProvidersPerCountryConstant
  InventoryAdjustmentReasons: Constant[]
  InventoryCategory: Constant[]
  InventoryCostTypes: Constant[]
  InventoryDistributors: Constant[]
  InventoryInStockUnit: Constant[]
  InventoryManufacturers: Constant[]
  InventoryOnHandChangeType: Constant[]
  InventoryOnHandStatus: Constant[]
  InventoryOrderStatus: ConstantWithColors[]
  InventoryPriceUnit: Constant[]
  InventoryProductSizeUnit: Constant[]
  InventoryShipmentItemStatus: ConstantWithColors[]
  InventoryShipmentSource: Constant[]
  InventoryShipmentStatus: ConstantWithColors[]
  InventoryStatuses: Constant[]
  InventorySubCategory: InventorySubCategoryConstant
  InventoryUploadItemMatchReasons: Constant[]
  InvoiceStates: ConstantWithColors[]
  LabOrderStatuses: Constant[]
  LabTestStates: ConstantWithColors[]
  LabTestTypes: Constant[]
  LabVendors: Constant[]
  LabelTemplates: Constant[]
  LabelTemplatesFormatted: Constant[]
  LabelTemplatesHtml: LabelTemplatesHtmlConstant[]
  LabsWidgetAssignedMode: WidgetAssignedModeConstant
  LandingWidgets: LandingWidgetsConstant[]
  Languages: LanguagesConstant[]
  LicenseTypes: Constant[]
  Locations: LocationsConstant
  MarketplaceCategory: Constant[]
  MarketplaceType: Constant[]
  MarketplaceWorkflow: Constant[]
  MeasurementTypes: MeasurementTypesConstant[]
  MentationTypes: MentationTypesConstant
  MucousMembraneColors: MucousMembraneColorsConstant
  MucousMembraneMoistures: MucousMembraneMoisturesConstant
  NoteType: Constant[]
  NotificationArea: Constant[]
  NotificationNotifyForItem: Constant[]
  NotificationStyle: Constant[]
  NotificationType: NotificationTypeConstant[]
  OnboardingPracticeType: Constant[]
  OrderFilters: OrderFiltersConstant[]
  PDMPReportVersions: PDMPReportVersionConstant[]
  PackageType: Constant[]
  PainScore: number[]
  PayGoOptionsList: Constant[]
  PayPassOptionsList: Constant[]
  PayStatusList: ConstantWithColors[]
  PaymentCardType: Constant[]
  PaymentMethod: Constant[]
  PaymentSourceType: Constant[]
  PaymentTransactionState: Constant[]
  PaymentType: Constant[]
  Pims: string[]
  PimsFieldToConstants: Constant[]
  PimsFull: Constant[]
  Placeholders: PlaceholderConstant[]
  PracticeCloningItems: PracticeCloningItemsConstant[]
  PracticeTypes: Constant[]
  PreferenceAttitudeTowardsStaff: Constant[]
  PreferenceCadenceOfVisits: Constant[]
  PreferenceCautions: Constant[]
  PreferenceClientPerformedTreatmentsLocations: Constant[]
  PreferenceDayOfWeek: Constant[]
  PreferenceDrugs: Constant[]
  PreferenceExtentOfCare: Constant[]
  PreferenceFinancialComfort: Constant[]
  PreferenceGenderIdentity: Constant[]
  PreferenceLabWorkFrequencies: Constant[]
  PreferenceMedicalAlerts: Constant[]
  PreferenceNeedsExtendedAppointmentTime: Constant[]
  PreferencePatientPerformedTreatmentsLocations: Constant[]
  PreferencePaymentMethod: Constant[]
  PreferencePremedicationVaccineReactions: Constant[]
  PreferenceSedations: Constant[]
  PreferenceServicePreferences: Constant[]
  PreferenceTimeOfDay: Constant[]
  PreferenceVenipunctures: Constant[]
  PrescriptionCompoundingReason: Constant[]
  PrescriptionProductSizeUnit: Constant[]
  PrescriptionStates: Constant[]
  PriceUnits: Constant[]
  ProblemBodySystemStates: Constant[]
  ProblemBodySystems: Constant[]
  ProblemEnums: ProblemEnumConstant[]
  ProcedureCategory: Constant[]
  ProcedurePriceUnit: Constant[]
  ProcedurePriceUnits: Constant[]
  ProcedureStatus: Constant[]
  ProductForm: Constant[]
  ProductSizeUnit: Constant[]
  PulseCharacterTypes: PulseCharacterTypesConstant
  RPCommunicationOptionsDefaultReminderMessage: string
  RabiesTagDefaultExpiration: RabiesTagDefaultExpirationConstant[]
  RefundDisabledReason: Constant[]
  RefundInvoiceState: Constant[]
  RefundReason: Constant[]
  ReminderProtocolTimeUnits: ReminderProtocolTimeUnitsConstant[]
  ReminderTemplateTimeUnits: ReminderTemplateTimeUnitsConstant[]
  RetailOrderStates: Constant[]
  ScheduleColumnNames: Constant[]
  ServiceAnimal: Constant[]
  Size: Constant[]
  SkinTurgors: SkinTurgorsConstant
  SoapAttachmentType: Constant[]
  SpaceType: Constant[]
  SpayedNeuteredStatus: Constant[]
  Species: SpeciesConstant[]
  StaticSoapWidget: Constant[]
  Tag: Constant[]
  TaskTemplateOptions: TaskTemplateOptionsConstant
  TaskTimeUnits: Constant[]
  TasksWidgetAssignedMode: WidgetAssignedModeConstant
  TemperatureUnits: TemperatureUnitsConstant[]
  TimeOffsets: Constant[]
  TimeUnits: Constant[]
  TimezoneGroups: TimezoneGroupConstant[]
  Timezones: TimezoneConstant[]
  VaccineDefaultAmount: Constant[]
  VaccineDefaultDeliveryLocation: Constant[]
  VaccineDefaultDoseType: Constant[]
  VaccineDefaultDurationOfImmunity: VaccineDefaultDurationOfImmunityConstant[]
  VaccineDeliveryMethod: Constant[]
  VaccineType: Constant[]
  VitalRecumbency: Constant[]
  WeightUnits: WeightUnitsConstant[]
  WorkFlowTypes: WorkFlowTypesConstant[]
  WplanCommunicationPromoteType: Constant[]
  WplanCoverAppointmentType: WplanCoverAppointmentTypeConstant[]
  WplanLimitType: Constant[]
  WplanPriceType: WplanPriceTypeConstant[]
  WplanType: Constant[]
  inventoryAdjustmentMethod: Constant[]
}

export type BillingAddress = BaseUser &
AddressParams & {
  apartmentNumber?: string
  id?: string
  street?: string
}

export type GenderRestriction = {
  genderId: string
  spayedNeuteredStatusId: string
}

export type QuestionAnswerInput = {
  id: string
  inputTypeId: string
  value: any
}

export type QuestionAnswer = {
  id: string
  inputs: QuestionAnswerInput[]
  selected: boolean
  text: string
  timestamp?: string | Nil
}

export type QuestionPriorAnswer = Omit<QuestionAnswer, 'selected'>

export type Question = {
  active?: boolean
  ageRangeMax?: number | Nil
  ageRangeMaxUnitId?: string | Nil
  ageRangeMin?: number | Nil
  ageRangeMinUnitId?: string | Nil
  answers: QuestionAnswer[]
  appointmentTypeIds: string[]
  businessId?: string
  frequencyId: string
  genderRestrictions: GenderRestriction[]
  id: string
  name: string
  patientIsNew: boolean
  priorAnswers?: QuestionPriorAnswer[]
  procedureIds: string[]
  questionText: string
  rank: number
  speciesIds: string[]
  weightMax: number
  weightMin: number
  workflowIds: string[]
}

export type UnsavedQuestion = Omit<Question, 'id'>

export enum BenefitPlanType {
  BASE = 'BASE',
  ADDON = 'ADDON',
}

export enum MembershipType {
  ADDON = 'ADDON',
  PACKAGE = 'PACKAGE',
  BASE = 'BASE',
  EXTRA = 'EXTRA',
}

export enum WellnessPlanCoverAppointmentTypes {
  NONE = 'NONE',
  REGULAR = 'REGULAR',
  TELEHEALTH = 'TELEHEALTH',
}

export type WellnessPlanCoverItem = {
  coverPercent?: number
  displayName?: string
  id?: string
  itemId: string | Nil
  itemType: string | Nil
  name: string | Nil
}

export type WellnessPlanBenefit = {
  appointmentTypeIds: string[]
  cover: WellnessPlanCoverItem[]
  coverAppointmentType?: WellnessPlanCoverAppointmentTypes
  coverCatalog?: boolean
  deletedCoverIds?: string[]
  description: string
  discountApplied?: boolean
  filledFromLevel?: number
  globalBenefitId?: string
  groupId: string
  id?: string
  level: number
  limit: number | Nil
  limitCustomizable: boolean
  limitReached: boolean
  limitTypeId: string
  name?: string
  planId: string
  planType: BenefitPlanType
  shouldShowFilledLabel?: boolean
  usages?: {
    creationDate: string
    id: string
    invoiceId: string
    name: string
  }[]
}

export type WellnessPlanPrice = {
  id: string
  price: number
  priceTypeId: string
}

export type UnsavedWellnessPlanPrice = Omit<WellnessPlanPrice, 'id'>

export type WellnessPlan = {
  autoRenewal: boolean | Nil
  autoRenewalModificationDatetime: string | Nil
  benefits: WellnessPlanBenefit[]
  deletedBenefitIds?: string[]
  deletedPriceIds?: string[]
  id: string
  level: number
  name: string
  planType: MembershipType
  planTypeId: string
  price: number
  priceType?: {
    displayName: string
  }
  priceTypeId: string
  prices: WellnessPlanPrice[]
  versionId?: string | Nil
}

export type UnsavedWellnessPlan = Omit<WellnessPlan, 'id' | 'prices'> & {
  prices: UnsavedWellnessPlanPrice[]
}

export type WellnessPlanVersion = {
  active: boolean
  activePatients?: number
  basePlanHidden?: boolean
  currentActiveId?: string
  deletedPlanIds?: string[]
  id: string
  oneTimeFeeAmount?: number
  plans: WellnessPlan[]
  versionNumber: number
}

export type UnsavedWellnessPlanVersion = Omit<WellnessPlanVersion, 'id'> & {
  id: null
}
